/**
 * Cart Drawer
 * ------------------------------------------------------------------
 * - Intercepts clicks on [data-header-cart-toggle] to open the drawer
 *   (falls through to /cart when drawer is not present, e.g. cart page).
 * - Listens for Empire's `cartcount:update` event (dispatched by
 *   ProductDetails / QuickShop after add-to-cart) to auto-open the drawer
 *   with fresh contents, replacing the atc-banner flyout.
 * - Suppresses any atc-banner instance appended to the header by
 *   empire-custom.js (AddToCartFlyout) via a MutationObserver.
 * - Handles line item quantity +/-/remove via /cart/change.js and refreshes
 *   the drawer via Section Rendering API.
 * - Notifies Easy Protection after refresh so its widget re-renders.
 */
(function () {
  'use strict';

  var DRAWER_ID = 'CartDrawer';
  var TOGGLE_ATTR = 'data-header-cart-toggle';
  var QUANTITY_DEBOUNCE_MS = 300;
  var isCartTemplate =
      document.body && document.body.classList.contains('template-cart');

  /* --------------------------------------------------------------------
   * Utilities
   * -------------------------------------------------------------------- */

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function on(target, event, selector, handler) {
    target.addEventListener(event, function (e) {
      var el = e.target.closest(selector);
      if (el && target.contains(el)) handler(e, el);
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (r) {
      return r.json();
    });
  }

  /* --------------------------------------------------------------------
   * Easy Protection hooks
   * -------------------------------------------------------------------- */

  /**
   * Reset Easy Protection loader locks so the app re-injects its widget
   * after we replace the drawer DOM. The loader booleans are set to `true`
   * once the app has rendered; flipping them back to `false` lets the
   * MutationObserver / re-init logic run again.
   */
  function notifyEasyProtection() {
    try {
      if (typeof window.__EASY_PROTECTION_LOADER__CART__ !== 'undefined') {
        window.__EASY_PROTECTION_LOADER__CART__ = false;
      }
      if (typeof window.__EASY_PROTECTION_LOADER__CART__IN !== 'undefined') {
        window.__EASY_PROTECTION_LOADER__CART__IN = false;
      }
    } catch (e) {
      /* noop */
    }

    // Broadcast events some app integrations listen to.
    // window.dispatchEvent(new CustomEvent('cart:refresh'));
    // window.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /* --------------------------------------------------------------------
   * Suppress the Empire ATC banner flyout
   * -------------------------------------------------------------------- */

  function suppressAtcBanner() {
    var kill = function (node) {
      // The template lives inside [data-templates] and must stay.
      if (node.closest && node.closest('[data-templates]')) return;
      if (node.parentNode) node.parentNode.removeChild(node);
    };

    // Kill any already-mounted instance.
    document
      .querySelectorAll('.atc-banner--container')
      .forEach(function (el) {
        kill(el);
      });

    // Observe future insertions (empire.js appends to the site header).
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('.atc-banner--container')) {
            kill(node);
          } else if (node.querySelectorAll) {
            node
              .querySelectorAll('.atc-banner--container')
              .forEach(function (el) {
                kill(el);
              });
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* --------------------------------------------------------------------
   * Drawer controller
   * -------------------------------------------------------------------- */

  function CartDrawer(root) {
    this.root = root;
    this.body = $('[data-cart-drawer-body]', root);
    this.overlay = $('[data-cart-drawer-overlay]', root);
    this.closeBtn = $('[data-cart-drawer-close]', root);
    this.panel = $('[data-cart-drawer-panel]', root);
    this.sectionId = root.getAttribute('data-section-id');
    this.pendingUpdate = null;

    this.bind();
  }

  CartDrawer.prototype.bind = function () {
    var self = this;

    // Close controls
    this.overlay.addEventListener('click', function () {
      self.close();
    });
    this.closeBtn.addEventListener('click', function () {
      self.close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen()) self.close();
    });

    // Delegated events inside the body (survive DOM swaps)
    on(this.body, 'click', '[data-cartitem-remove]', function (e, el) {
      e.preventDefault();
      var row = el.closest('[data-cartitem-key]');
      if (!row) return;
      row.classList.add('removing');
      self.updateItem(row.getAttribute('data-cartitem-key'), 0);
    });

    on(this.body, 'click', '[data-quantity-plus]', function (e, btn) {
      e.preventDefault();
      self.stepQuantity(btn, 1);
    });

    on(this.body, 'click', '[data-quantity-minus]', function (e, btn) {
      e.preventDefault();
      self.stepQuantity(btn, -1);
    });

    // Direct input edits (typed value)
    on(
      this.body,
      'change',
      '[data-quantity-input]',
      debounce(function (e, input) {
        var row = input.closest('[data-cartitem-key]');
        if (!row) return;
        var qty = Math.max(0, parseInt(input.value, 10) || 0);
        self.updateItem(row.getAttribute('data-cartitem-key'), qty);
      }, QUANTITY_DEBOUNCE_MS)
    );

    // Prevent the form from actually POSTing to /cart when hitting Enter
    // on quantity inputs; the "Checkout" submit still works because it has
    // name="checkout".
    this.body.addEventListener('submit', function (e) {
      var submitter = e.submitter;
      if (submitter && submitter.name === 'checkout') return;
      e.preventDefault();
    });
  };

  CartDrawer.prototype.isOpen = function () {
    return this.root.classList.contains('is-open');
  };

  CartDrawer.prototype.open = function () {
    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    document.documentElement.style.overflow = 'hidden';
    // Focus close button for a11y
    var self = this;
    setTimeout(function () {
      self.closeBtn && self.closeBtn.focus();
    }, 100);
  };

  CartDrawer.prototype.close = function () {
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    document.documentElement.style.overflow = '';
  };

  /**
   * Adjust quantity via + / - buttons. Reads current input value to avoid
   * relying on the button's disabled state. Decrementing below 1 removes
   * the line item.
   */
  CartDrawer.prototype.stepQuantity = function (btn, delta) {
    var row = btn.closest('[data-cartitem-key]');
    if (!row) return;
    var input = row.querySelector('[data-quantity-input]');
    if (!input) return;
    var current = parseInt(input.value, 10) || 0;
    var next = Math.max(0, current + delta);
    input.value = next;
    if (next === 0) row.classList.add('removing');
    this.updateItem(row.getAttribute('data-cartitem-key'), next);
  };

  /**
   * POST /cart/change.js then refresh the drawer contents.
   */
  CartDrawer.prototype.updateItem = function (key, quantity) {
    var self = this;
    var requestId = (this.pendingUpdate = {});
    var url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url
        ? window.Theme.routes.cart_change_url
        : '/cart/change') + '.js';

    this.root.classList.add('is-loading');

    fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (data) {
        if (requestId !== self.pendingUpdate) return;

        return self.refresh(data).then(function () {
          window.dispatchEvent(
            new CustomEvent('cartcount:update', { detail: data })
          );
        });
      })
      .catch(function (err) {
        console.error('[cart-drawer] update failed', err);
      })
      .then(function () {
        self.root.classList.remove('is-loading');
        if (requestId === self.pendingUpdate) self.pendingUpdate = null;
      });
  };

  /**
   * Refresh drawer body via Section Rendering API.
   *
   * Replaces the entire drawer body with fresh HTML from the server, then
   * notifies Easy Protection so it re-initialises its widget and writes
   * the correct subtotal (raw total + protection).
   */
  CartDrawer.prototype.refresh = function (cartData) {
    var self = this;
    var url = '/?section_id=' + encodeURIComponent(this.sectionId);

    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();

        var freshBody =
          wrapper.querySelector('[data-cart-drawer-body]') ||
          wrapper.firstElementChild;
        var freshCount = wrapper.querySelector('[data-cart-drawer-count]');

        if (!freshBody) return;

        // ---- Full DOM replacement ----
        self.body.innerHTML = freshBody.innerHTML;

        // Notify Easy Protection so it re-initialises its widget and
        // recalculates the protection price on top of the fresh total.
        notifyEasyProtection();

        // ---- Count badge ----
        if (freshCount) {
          var mine = $('[data-cart-drawer-count]', self.root);
          if (mine) mine.textContent = freshCount.textContent;
        }
      })
      .catch(function () {
        // On a hard failure, fall back to a full reload to keep cart in sync.
        window.location.reload();
      });
  };

  /* --------------------------------------------------------------------
   * Bootstrapping
   * -------------------------------------------------------------------- */

  function init() {
    suppressAtcBanner();

    var root = document.getElementById(DRAWER_ID);
    if (!root) return; // /cart template does not render the drawer

    var drawer = new CartDrawer(root);
    window.CartDrawer = drawer;

    // Header cart button — open drawer instead of navigating.
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[' + TOGGLE_ATTR + ']');
      if (!toggle) return;
      // Allow modifier-click / middle-click / target=_blank to navigate.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      if (isCartTemplate) return; // no drawer on /cart template
      e.preventDefault();
      drawer.open();
    });

    // Add-to-cart from PDP / QuickShop dispatches this event with cart JSON.
    window.addEventListener('cartcount:update', function (e) {
      // Ignore self-triggered events dispatched inside updateItem();
      // they are already followed by our own refresh().
      if (drawer.pendingUpdate) return;
      if (isCartTemplate) return; // no drawer on /cart template
      drawer.refresh(e.detail).then(function () {
        drawer.open();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
